import { memo, useState, useCallback, useRef } from "react";
import { TextField } from "@mui/material";
import { SingleValueCardColor, SingleValueCardType } from "constants/index";

export interface EditableTextFieldProps {
  value: number;
  formattedValue: string;
  // Whether `value` is a real figure. The cards resolve this themselves and
  // state it, rather than leaving this field to infer it from the formatted
  // string: a refused CURRENCY card displays "$0.00", which is
  // indistinguishable from a real $0.00, so reading the display could not
  // tell them apart. Two behaviours below depend on this and both were wrong
  // while it was inferred -- see the comments on each.
  hasFigure: boolean;
  type: SingleValueCardType;
  displayColor: SingleValueCardColor;
  onValueChange: (value: number) => void;
  // Id of the node explaining why this field has no figure. It lands on the
  // input itself: the tooltip carrying the same reason describes the wrapper
  // around this field, which a screen reader on the input never reaches.
  describedById?: string;
  // Withdraws the override entirely: the field still shows its figure and is
  // still reachable by tab, but no interaction starts an edit. The card sets
  // it for a tile it is dimming (SingleValueCard says why the two travel
  // together), and it has to be honoured on all THREE ways in below --
  // focus, click and keydown -- because each of them starts an edit on its
  // own, and a native `readOnly` on the input only stops the typing that
  // follows. Left out, the field a reader cannot see would still take their
  // keystrokes and save a figure over the scope's own.
  readOnly?: boolean;
  // What an EMPTIED box means, for the one row that has an answer to that
  // question. Left out — which is every other field on these cards — clearing
  // still does exactly what it always did: the empty string parses to NaN
  // below, nothing is written, and the previous figure stays. That silence is
  // right for a persisted field, where "" is a slip rather than an
  // instruction and the alternative is destroying a saved number on a stray
  // keystroke.
  //
  // It is wrong for a field whose default is a STATE rather than a number.
  // Available Margin Balance reads N/A until somebody types into it, and
  // without this there is no way back to that: a figure typed by mistake
  // would keep printing in the client's PDF until the page was reloaded. So
  // the row that has a meaning for "empty" supplies it, and nothing else
  // changes behaviour.
  //
  // It fires for a BLANK box only, never for unparseable text: "abc" is a
  // typo, not an instruction to clear, and treating it as one would throw
  // away a figure over a mistyped character.
  onClear?: () => void;
}

const EditableTextField = memo(({
  value,
  formattedValue,
  hasFigure,
  type,
  displayColor,
  onValueChange,
  describedById,
  readOnly = false,
  onClear,
}: EditableTextFieldProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState("");
  
  const inputRef = useRef<HTMLInputElement>(null);
  const isCancelledRef = useRef(false);

  const handleFocus = useCallback(() => {
    // The first of the three doors, and the one the other two come through:
    // `onClick` calls this directly, and `handleKeyDown` calls it for the
    // keystroke that begins an edit.
    if (readOnly) return;
    if (!isEditing) {
      isCancelledRef.current = false;
      setIsEditing(true);
      // Seeding value.toString() for a field that has no figure would put a
      // bare 0 on screen the moment it is focused -- the cards pass 0 down for
      // the blank sentinel and for an unavailable figure alike -- and 0 is the
      // exact assertion both states exist to avoid: a tabbing user would read
      // a participation rate of zero where there is no rate at all. An empty
      // box says what the display said and still takes what the user types;
      // left untyped it parses to NaN below and writes nothing, as before.
      setLocalValue(hasFigure ? value.toString() : "");
    }
  }, [isEditing, value, hasFigure, readOnly]);

  const saveValue = useCallback(() => {
    const cleanString = localValue.replace(/[$,%+\s]/g, "");

    // An emptied box, for the row that says what that means. Read BEFORE the
    // parse rather than out of its NaN branch, because the two are not the
    // same question: "" and "abc" both fail to parse, and only the first is
    // somebody clearing the field. Currency and percent marks are already
    // stripped above, so a box left holding "$" is empty too — there is no
    // figure in it either way.
    if (cleanString === "") {
      onClear?.();
      return;
    }

    let parsedValue = parseFloat(cleanString);

    if (!isNaN(parsedValue)) {
      if (type === SingleValueCardType.CURRENCY) {
        parsedValue = parseFloat(parsedValue.toFixed(3)); 
      } else if (type === SingleValueCardType.PERCENTAGE) {
        parsedValue = parseFloat(parsedValue.toFixed(2)); 
      }

      // The equality check is a no-op guard for an unchanged figure, and it
      // must not apply when there is no figure to be unchanged FROM: the
      // cards hand 0 down for every non-figure, so typing a genuine 0 over an
      // N/A compared equal and was silently discarded. Zero is a real report
      // figure -- a flat quarter, no margin interest -- and on the strategy
      // report it is also the only way to clear the N/A, since the reason is
      // dropped inside onValueChange.
      if (!hasFigure || parsedValue !== value) {
        onValueChange(parsedValue);
      }
    }
  }, [localValue, type, value, hasFigure, onValueChange, onClear]);

  const handleBlur = useCallback(() => {
    if (isEditing && !isCancelledRef.current) {
      saveValue();
    }
    setIsEditing(false);
  }, [isEditing, saveValue]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Escape still blurs, so a keyboard user is never trapped on a field
      // that answers nothing else.
      if (readOnly) {
        if (e.key === "Escape") inputRef.current?.blur();
        return;
      }
      if (e.key === "Enter") {
        if (isEditing) {
          saveValue();
          setIsEditing(false)
        }
      } else if (e.key === "Escape") {
        isCancelledRef.current = true;
        inputRef.current?.blur();
      } else if (!isEditing) {
          isCancelledRef.current = false;
          setIsEditing(true);
          handleFocus();
      }
    },
    [isEditing, saveValue, handleFocus, readOnly]
  );

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  }, []);

  return (
    <TextField
      value={isEditing ? localValue : formattedValue}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onClick={handleFocus}
      onChange={handleChange}
      variant="standard"
      inputRef={inputRef}
      inputProps={{ "aria-describedby": describedById }}
      InputProps={{
        disableUnderline: true,
        // Announced by assistive tech and the reason the field is still
        // focusable rather than `disabled`: a disabled input leaves the tab
        // order, and a reader who navigates by keyboard would lose the
        // figure altogether rather than merely be unable to change it.
        readOnly,
        sx: {
          fontSize: "1.5rem",
          fontWeight: 500,
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          lineHeight: 1.2,
          color: (theme) => theme.palette[displayColor]?.main || displayColor,
          "& input": {
            textAlign: "left",
            padding: 0,
            // The text caret invites an edit that will not happen; the
            // default arrow says the figure is there to be read.
            ...(readOnly ? { cursor: "default" } : {}),
          },
        },
      }}
      sx={{
        "& .MuiInputBase-root": {
          fontSize: "1.5rem",
        },
      }}
    />
  );
});

EditableTextField.displayName = "EditableTextField";
export default EditableTextField;
